import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  StarIcon,
  ChartBarIcon,
  BanknotesIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
  FireIcon,
  TrophyIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  BoltIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

interface Partner {
  id: string;
  name: string;
  nameAr: string;
  logo: string;
  category: 'marketing' | 'software' | 'logistics' | 'finance' | 'communication' | 'cloud';
  description: string;
  descriptionAr: string;
  discountRate: number;
  estimatedSavings: number;
  actualSavings: number;
  usageCount: number;
  rating: number;
  features: string[];
  featuresAr: string[];
  aiRecommendation: {
    score: number;
    reason: string;
    reasonAr: string;
    urgency: 'low' | 'medium' | 'high' | 'critical';
    potentialROI: number;
    implementationTime: string;
    implementationTimeAr: string;
  };
  trends: {
    month: string;
    savings: number;
    usage: number;
  }[];
  status: 'active' | 'recommended' | 'new' | 'trending';
  contractType: 'monthly' | 'annual' | 'usage-based';
  minSpend: number;
  maxDiscount: number;
}

interface PartnerDetailsModalProps {
  partner: Partner | null;
  isOpen: boolean;
  onClose: () => void;
}

const PartnerDetailsModal: React.FC<PartnerDetailsModalProps> = ({ partner, isOpen, onClose }) => {
  if (!partner) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical': return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
      case 'high': return 'text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'low': return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'from-green-500 to-green-600';
      case 'recommended': return 'from-blue-500 to-purple-600';
      case 'new': return 'from-orange-500 to-red-500';
      case 'trending': return 'from-pink-500 to-rose-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'marketing': return <ChartBarIcon className="w-6 h-6" />;
      case 'software': return <BoltIcon className="w-6 h-6" />;
      case 'logistics': return <TrophyIcon className="w-6 h-6" />;
      case 'finance': return <CurrencyDollarIcon className="w-6 h-6" />;
      case 'communication': return <UserGroupIcon className="w-6 h-6" />;
      case 'cloud': return <ShieldCheckIcon className="w-6 h-6" />;
      default: return <BoltIcon className="w-6 h-6" />;
    }
  };

  const getCategoryNameAr = (category: string) => {
    switch (category) {
      case 'marketing': return 'التسويق';
      case 'software': return 'البرمجيات';
      case 'logistics': return 'اللوجستيات';
      case 'finance': return 'المالية';
      case 'communication': return 'الاتصالات';
      case 'cloud': return 'الحوسبة السحابية';
      default: return 'عام';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`bg-gradient-to-r ${getStatusColor(partner.status)} p-6 text-white`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <div className="text-6xl">{partner.logo}</div>
                  <div>
                    <h2 className="text-3xl font-bold">{partner.nameAr}</h2>
                    <p className="text-lg opacity-90">{partner.name}</p>
                    <div className="flex items-center space-x-2 rtl:space-x-reverse mt-2">
                      {getCategoryIcon(partner.category)}
                      <span className="text-sm">{getCategoryNameAr(partner.category)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <div className="text-center">
                    <div className="text-4xl font-bold">{partner.aiRecommendation.score}</div>
                    <div className="text-sm opacity-75">نقاط AI</div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <XMarkIcon className="w-8 h-8" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-8">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4"
                >
                  <div className="flex items-center justify-between">
                    <BanknotesIcon className="w-8 h-8 text-green-600" />
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-600 dark:text-gray-400">التوفير الفعلي</div>
                      <div className="text-2xl font-bold text-green-600">{formatCurrency(partner.actualSavings)}</div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-4"
                >
                  <div className="flex items-center justify-between">
                    <StarIcon className="w-8 h-8 text-blue-600" />
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-600 dark:text-gray-400">التقييم</div>
                      <div className="text-2xl font-bold text-blue-600">{partner.rating}/5</div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4"
                >
                  <div className="flex items-center justify-between">
                    <UserGroupIcon className="w-8 h-8 text-purple-600" />
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-600 dark:text-gray-400">المستخدمون</div>
                      <div className="text-2xl font-bold text-purple-600">{partner.usageCount.toLocaleString()}</div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl p-4"
                >
                  <div className="flex items-center justify-between">
                    <FireIcon className="w-8 h-8 text-orange-600" />
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-600 dark:text-gray-400">خصم</div>
                      <div className="text-2xl font-bold text-orange-600">{partner.discountRate}%</div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* AI Recommendation */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-6">
                <div className="flex items-start space-x-4 rtl:space-x-reverse">
                  <SparklesIcon className="w-8 h-8 text-purple-600 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-purple-900 dark:text-purple-300 mb-3">
                      توصية الذكاء الاصطناعي المتقدمة
                    </h3>
                    <p className="text-purple-700 dark:text-purple-400 mb-4 leading-relaxed">
                      {partner.aiRecommendation.reasonAr}
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                        <div className="text-sm text-gray-600 dark:text-gray-400">الأولوية</div>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getUrgencyColor(partner.aiRecommendation.urgency)}`}>
                          {partner.aiRecommendation.urgency === 'critical' ? 'حرج' :
                           partner.aiRecommendation.urgency === 'high' ? 'عالي' :
                           partner.aiRecommendation.urgency === 'medium' ? 'متوسط' : 'منخفض'}
                        </span>
                      </div>
                      
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                        <div className="text-sm text-gray-600 dark:text-gray-400">العائد المتوقع</div>
                        <div className="text-lg font-bold text-green-600">{partner.aiRecommendation.potentialROI}%</div>
                      </div>
                      
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                        <div className="text-sm text-gray-600 dark:text-gray-400">وقت التنفيذ</div>
                        <div className="text-lg font-bold text-blue-600">{partner.aiRecommendation.implementationTimeAr}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">المميزات الرئيسية</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {partner.featuresAr.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center space-x-3 rtl:space-x-reverse bg-gray-50 dark:bg-gray-700 rounded-lg p-3"
                    >
                      <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Trends Chart */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">اتجاهات التوفير والاستخدام</h3>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={partner.trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value, name) => [
                          name === 'savings' ? formatCurrency(value as number) : `${value}%`,
                          name === 'savings' ? 'التوفير' : 'الاستخدام'
                        ]}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="savings" 
                        stroke="#10b981" 
                        strokeWidth={3}
                        dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="usage" 
                        stroke="#3b82f6" 
                        strokeWidth={3}
                        dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Contract Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4">تفاصيل العقد</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">نوع العقد</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {partner.contractType === 'monthly' ? 'شهري' :
                         partner.contractType === 'annual' ? 'سنوي' : 'حسب الاستخدام'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">الحد الأدنى للإنفاق</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(partner.minSpend)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">أقصى خصم</span>
                      <span className="font-medium text-gray-900 dark:text-white">{partner.maxDiscount}%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4">إحصائيات الأداء</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">التوفير المتوقع</span>
                      <span className="font-medium text-green-600">{formatCurrency(partner.estimatedSavings)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">التوفير الفعلي</span>
                      <span className="font-medium text-green-600">{formatCurrency(partner.actualSavings)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">نسبة تحقيق الهدف</span>
                      <span className="font-medium text-blue-600">
                        {Math.round((partner.actualSavings / partner.estimatedSavings) * 100)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-4 rtl:space-x-reverse pt-6 border-t border-gray-200 dark:border-gray-600">
                <button
                  onClick={onClose}
                  className="px-6 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                >
                  إغلاق
                </button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg"
                >
                  تفعيل الشراكة
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg"
                >
                  طلب عرض سعر
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PartnerDetailsModal;
